import { useState } from 'react';
import { DEFAULT_ENDPOINT_URL_EXPORT } from '../graphql/client';
import { Download, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

function formatDocDate(dateStr: string | null) {
  if (!dateStr) return '–';
  const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin',
  });
}

export const UserDocumentPanel = ({ categoryName, documents, token }: {
  categoryName: string;
  documents: { id: string; valid_from: string | null; valid_until: string | null; created_at: string; file_path: string }[];
  token: string;
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const latest = documents[0];
  const older = documents.slice(1);

  const downloadDoc = async (docId: string) => {
    try {
      const response = await fetch(
        `${DEFAULT_ENDPOINT_URL_EXPORT}/documents/${docId}/download?t=${Date.now()}`,
        { method: 'GET', headers: { Authorization: token, 'Cache-Control': 'no-cache' } }
      );
      if (!response.ok) {
        toast.error('Dokument konnte nicht geöffnet werden.');
        return;
      }
      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(pdfUrl, '_blank');
      window.URL.revokeObjectURL(pdfUrl);
    } catch (err) {
      console.error(err);
    }
  };

  const StatusBadge = ({ valid_until, small = false }: { valid_until: string | null; small?: boolean }) => {
    const base = `inline-flex items-center gap-1 rounded-full font-medium ${small ? 'px-3 py-1 text-[13px]' : 'px-4 py-2 text-[16px]'}`;
    if (!valid_until) return <span className={`${base} bg-[#e8f5e9] text-[#2e7d32]`}>Kein Ablaufdatum</span>;
    const expired = new Date(valid_until) < new Date();
    const diff = new Date(valid_until).getTime() - Date.now();
    const soon = !expired && diff < 60 * 24 * 60 * 60 * 1000;
    if (expired) return <span className={`${base} bg-[#fdecea] text-[#c62828]`}>Abgelaufen</span>;
    if (soon) return <span className={`${base} bg-[#fff8e1] text-[#f57f17]`}>Läuft bald ab</span>;
    return <span className={`${base} bg-[#e8f5e9] text-[#2e7d32]`}>Gültig</span>;
  };

  const renderRow = (doc: typeof documents[0], index: number) => (
    <div key={doc.id}>
      {/* Desktop */}
      <div className="hidden xl:grid grid-cols-[1fr_160px_180px_180px_120px] gap-4 px-4 py-3 items-center hover:bg-[#f9f9f9] transition-colors bg-white">
        <div className="text-[16px] text-[#1c1c1c]">
          {index === 0 ? 'Aktuelle Version' : `Version ${documents.length - index}`}
        </div>
        <div>
          <StatusBadge valid_until={doc.valid_until} />
        </div>
        <div className="text-[16px] text-[#6b6b6b]">{formatDocDate(doc.created_at)}</div>
        <div className="text-[16px] text-[#6b6b6b]">
          {doc.valid_from || doc.valid_until
            ? `${formatDocDate(doc.valid_from)} – ${formatDocDate(doc.valid_until)}`
            : '–'}
        </div>
        <div>
          <button
            onClick={() => downloadDoc(doc.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-white bg-[#0084c7] hover:bg-[#006fa8] rounded-lg transition-colors"
          >
            <Download className="size-4" />
            Öffnen
          </button>
        </div>
      </div>

{/* Mobile */}
      <div className="flex xl:hidden flex-col gap-3 px-4 py-3 bg-white hover:bg-[#f9f9f9] transition-colors">
        <div className="flex items-center justify-between">
          <span className="text-[16px] font-medium text-[#1c1c1c]">
            {index === 0 ? 'Aktuelle Version' : `Version ${documents.length - index}`}
          </span>
          <StatusBadge valid_until={doc.valid_until} small />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <div className="text-[13px] text-[#9ca3af]">Hochgeladen</div>
            <div className="text-[14px] text-[#1c1c1c]">{formatDocDate(doc.created_at)}</div>
            {(doc.valid_from || doc.valid_until) && (
              <>
                <div className="text-[13px] text-[#9ca3af] mt-2">Gültigkeitszeitraum</div>
                <div className="text-[14px] text-[#1c1c1c]">
                  {formatDocDate(doc.valid_from)} – {formatDocDate(doc.valid_until)}
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => downloadDoc(doc.id)}
            className="flex items-center justify-center p-2 text-white bg-[#0084c7] hover:bg-[#006fa8] rounded-lg transition-colors flex-shrink-0"
          >
            <Download className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="overflow-visible">
      {/* Header Desktop */}
      <div className="hidden xl:grid grid-cols-[1fr_160px_180px_180px_120px] gap-4 px-4 py-3 bg-[#f9f9f9] rounded-lg text-[14px] font-medium text-[#6b6b6b] mt-4 first:mt-0">
        <div className="flex items-center gap-2 text-[#1c1c1c] font-semibold">
          {categoryName}
          <span className="text-[11px] font-normal text-[#9ca3af]">
            {documents.length} Version{documents.length !== 1 ? 'en' : ''}
          </span>
        </div>
        <div>Status</div>
        <div>Hochgeladen</div>
        <div>Zeitraum</div>
        <div></div>
      </div>

      {/* Header Mobile */}
      <div className="flex xl:hidden items-center gap-2 px-4 py-3 bg-[#f9f9f9] rounded-lg mt-4 first:mt-0">
        <span className="text-[15px] font-semibold text-[#1c1c1c]">{categoryName}</span>
        <span className="text-[11px] text-[#9ca3af]">
          {documents.length} Version{documents.length !== 1 ? 'en' : ''}
        </span>
      </div>

      {latest && renderRow(latest, 0)}

      {older.length > 0 && (
        <>
          <button
            onClick={() => setHistoryOpen(p => !p)}
            className="w-full flex items-center gap-2 px-4 py-2 text-[13px] text-[#6b6b6b] hover:text-[#1c1c1c] hover:bg-[#f5f5f5] transition-colors border-t border-[#f0f0f0]"
          >
            <ChevronRight className={`size-4 transition-transform ${historyOpen ? 'rotate-90' : ''}`} />
            {historyOpen ? '' : `${older.length} ältere Version${older.length !== 1 ? 'en' : ''} anzeigen`}
          </button>
          {historyOpen && older.map((doc, i) => renderRow(doc, i + 1))}
        </>
      )}
    </div>
  );
};